import { useEffect, useRef, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type {
  Message,
  ClarificationResponse,
  ClarificationOption,
  StreamingState,
} from '../../types';
import type { ReactNode } from 'react';
import { ChatMessage } from './ChatMessage';
import { StreamingMessage } from './StreamingMessage';
import { TypingIndicator } from './TypingIndicator';
import { HistoryLoadingIndicator } from './HistoryLoadingIndicator';
import { ClarificationPrompt } from './ClarificationPrompt';
import { useKeyboardVisible } from '../../hooks/useKeyboardVisible';
import { Button } from '@/components/ui/button';
import { ArrowDown } from 'lucide-react';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  isLoadingHistory?: boolean;
  onSendPrompt?: (prompt: string) => void;
  hidePrompts?: boolean;
  emptyStateChatInput?: ReactNode;
  userEmail?: string;
  /** Optional clarification prompt to show as ephemeral UI within the conversation */
  clarification?: ClarificationResponse | null;
  /** Handler when user selects a clarification option */
  onClarificationSelect?: (option: ClarificationOption) => void;
  /** Handler when user dismisses clarification to rephrase their query */
  onClarificationDismiss?: () => void;
  /** Streaming state for partial content during streaming responses */
  streamingState?: StreamingState;
  /** Handler to cancel an in-progress streaming request */
  onCancelStreaming?: () => void;
}

export const MessageList = ({
  messages,
  isLoading,
  isLoadingHistory = false,
  onSendPrompt,
  hidePrompts = false,
  emptyStateChatInput,
  userEmail,
  clarification,
  onClarificationSelect,
  onClarificationDismiss,
  streamingState,
  onCancelStreaming,
}: MessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isKeyboardVisible = useKeyboardVisible();

  // Check if user is near bottom
  const checkIfNearBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return true;

    const threshold = 100; // pixels from bottom
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold;

    return isNearBottom;
  };

  // Check if content overflows (needs scrolling)
  const checkOverflow = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const contentOverflows = container.scrollHeight > container.clientHeight;
    setHasOverflow(contentOverflows);
  }, []);

  // Handle scroll event
  const handleScroll = () => {
    const isNearBottom = checkIfNearBottom();
    setShowScrollButton(!isNearBottom && messages.length > 0);
    checkOverflow();
  };

  // Check overflow when messages change or keyboard visibility changes
  useEffect(() => {
    checkOverflow();
  }, [messages, isLoading, isKeyboardVisible, checkOverflow]);

  // Auto-scroll to bottom on new messages or clarification prompt
  // During streaming, scroll instantly to avoid jarring smooth-scroll animations
  useEffect(() => {
    const isStreaming = streamingState?.isStreaming;
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
    });
  }, [messages, isLoading, clarification, streamingState?.isStreaming]);

  // Scroll during streaming content updates (throttled via requestAnimationFrame)
  useEffect(() => {
    if (streamingState?.isStreaming && streamingState.content) {
      // Use instant scroll during active streaming to prevent animation buildup
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }
  }, [streamingState?.content, streamingState?.isStreaming]);

  // Scroll to bottom handler
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Track animation state when hidePrompts changes
  useEffect(() => {
    if (messages.length === 0) {
      // Schedule state update to avoid cascading renders
      queueMicrotask(() => {
        setIsAnimating(true);
      });
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300); // Match the animation duration
      return () => clearTimeout(timer);
    } else {
      // Ensure scrolling is enabled when messages exist
      queueMicrotask(() => {
        setIsAnimating(false);
      });
    }
  }, [hidePrompts, messages.length]);

  const examplePrompts = [
    "Summarize last week's sales",
    'Who is our top-performing salesperson?',
    'What is our marketing ROI?',
  ];

  return (
    <>
      {/* Scroll to bottom button - floats above content */}
      <AnimatePresence>
        {showScrollButton && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 lg:bottom-28 left-1/2 transform -translate-x-1/2 z-30"
          >
            <Button
              onClick={scrollToBottom}
              variant="outline"
              size="icon"
              className="rounded-full shadow-lg"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="h-5 w-5" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`flex-1 py-4 flex flex-col ${
          isAnimating ? 'overflow-hidden' : 'overflow-y-auto'
        } ${
          messages.length > 0 && isKeyboardVisible && !hasOverflow
            ? 'justify-end'
            : ''
        }`}
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
      >
        {/* Show history loading indicator when fetching chat history */}
        {isLoadingHistory && messages.length === 0 ? (
          <div
            className="flex-1 flex items-center justify-center w-full"
            role="status"
            aria-live="polite"
          >
            <HistoryLoadingIndicator />
          </div>
        ) : messages.length === 0 && !isLoading ? (
          <div
            className={`flex-1 flex ${isKeyboardVisible ? 'items-end pb-4' : 'items-center'} justify-center w-full`}
            role="region"
            aria-label="Chat welcome message"
          >
            <div className="w-full max-w-3xl mx-auto px-2 sm:px-4">
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  layout: { duration: 0.3, ease: 'easeInOut' },
                }}
                className="flex flex-col items-center justify-center"
              >
                {/* Welcome Message - Animates position */}
                <motion.h1
                  className="tracking-tight text-3xl font-bold leading-tight text-center px-4"
                  animate={{
                    paddingBottom: hidePrompts ? '0px' : '32px',
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  Hello! I'm your AI business analyst. What would you like to
                  know?
                </motion.h1>

                {/* Desktop Chat Input - Between sections */}
                {emptyStateChatInput && (
                  <div className="hidden sm:block w-full max-w-3xl px-4 py-6">
                    {emptyStateChatInput}
                  </div>
                )}

                {/* Section Header and Prompts - Animated show/hide */}
                <AnimatePresence>
                  {!hidePrompts && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <motion.h4
                        className="text-muted-foreground text-sm font-bold uppercase leading-normal tracking-widest py-2 text-center"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        Try asking...
                      </motion.h4>

                      {/* Example Prompts Chips */}
                      <motion.div
                        className="flex flex-wrap items-center justify-center gap-2 p-3"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                      >
                        {examplePrompts.map((prompt, index) => (
                          <motion.div
                            key={prompt}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                          >
                            <Button
                              variant="secondary"
                              onClick={() => onSendPrompt?.(prompt)}
                              className="h-10"
                            >
                              {prompt}
                            </Button>
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-3xl mx-auto px-2 sm:px-4">
            <div className="space-y-6">
              {/* Messages render without layout animations for smooth streaming */}
              <AnimatePresence mode="sync">
                {messages.map((message, index) => (
                  <ChatMessage
                    key={`${message.role}-${message.timestamp}-${index}`}
                    message={message}
                    userEmail={userEmail}
                  />
                ))}
              </AnimatePresence>

              {/* Streaming message - shows partial content during streaming */}
              {streamingState?.isStreaming && streamingState.content && (
                <StreamingMessage
                  content={streamingState.content}
                  onCancel={onCancelStreaming}
                />
              )}

              {/* Only show typing indicator when loading but not streaming */}
              {isLoading && !streamingState?.isStreaming && <TypingIndicator />}

              {/* Ephemeral clarification prompt - appears within conversation flow */}
              <AnimatePresence>
                {clarification &&
                  onClarificationSelect &&
                  onClarificationDismiss && (
                    <ClarificationPrompt
                      clarification={clarification}
                      onSelect={onClarificationSelect}
                      onDismiss={onClarificationDismiss}
                      isLoading={isLoading}
                    />
                  )}
              </AnimatePresence>
            </div>

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
    </>
  );
};
